import React, { useCallback } from 'react';
import { IStyle, Panel, PanelType, mergeStyles } from '@fluentui/react';
import { BwjButton, IBwjButtonProps } from '../bwj-button';
import { BwjScrollContainer } from '../bwj-scroll-container';
import { BwjSemiFlexContainer } from '../bwj-semi-flex-container';
import { isTouchDevice } from '../../utils/device';
import { BwjEventArgs } from '../../entities/bwj-event';


interface IBwjSidePanelProps {
    showPanel: boolean;
    title?: string;
    panelType?: PanelType;
    footerButtons?: IBwjButtonProps[];
    customFooter?: JSX.Element | undefined;
    onDismiss: () => void;
    closeButtonAriaLabel?: string;
    /**see BwjScrollContainer documentation */
    contentStyle?: IStyle | undefined;
    invokeScrollReset?: BwjEventArgs<null> | undefined;
    invokeScrollTo?: BwjEventArgs<{selector: string, offset?: number}> | undefined;
    invokeScrollPositionReport?: BwjEventArgs<any> | undefined;
    onScrollChanged?: (scrollTop: number, scrollLeft: number, containerWidth: number, containerHeight: number, scrollContainerSelector: string, context?: any) => void | undefined;
}

const panelStyles = {
  main: {
    '@media (min-width: 480px)': {
      width: 480,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    padding: 0,
    width: '100%',
    flexGrow: 1,
  }
};

const scrollablePanelClass = mergeStyles([
  {
    '& div[class*="scrollableContent"]': {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    }
  },
]);

const scrollButtonPaddingClass = mergeStyles({
  width: '100%',
  height: 0,
  marginBottom: 96,
});

const footerButtonTrayClass = mergeStyles([
  {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 20,
  },
]);

const touchEnabled = isTouchDevice();

export const BwjSidePanel: React.FunctionComponent<IBwjSidePanelProps> = (props) => {

  const { footerButtons, onDismiss, customFooter } = props;
  const usingFooterButtons = !!footerButtons && footerButtons.length > 0;

  const onRenderFooterContent = useCallback(() => (
    <>
      {usingFooterButtons &&
      <div className={footerButtonTrayClass}>
        {footerButtons!.map((btnProps, index) => 
          <BwjButton key={index} {...btnProps} />
        )}
      </div>}
      {!!customFooter && customFooter}
    </>
  ), [usingFooterButtons, footerButtons, customFooter]);

  return (
    <Panel
      title={props.title}
      headerText={props.title}
      isOpen={props.showPanel}
      isLightDismiss={false}
      onDismiss={onDismiss}
      className={scrollablePanelClass}
      styles={panelStyles}
      type={props.panelType ?? PanelType.medium}
      closeButtonAriaLabel={props.closeButtonAriaLabel ?? 'Close'}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom={usingFooterButtons}
      allowTouchBodyScroll={touchEnabled}
    >
      <BwjSemiFlexContainer
        pinHeight={true}
        styles={{ root: { flexGrow: 1, }}}
      >
        <BwjScrollContainer
          containerStyle={props.contentStyle}
          invokeScrollPositionReport={props.invokeScrollPositionReport}
          invokeScrollReset={props.invokeScrollReset}
          invokeScrollTo={props.invokeScrollTo}
          onScrollChanged={props.onScrollChanged}
        >
          {props.children}
          <div className={scrollButtonPaddingClass}></div>
        </BwjScrollContainer>
      </BwjSemiFlexContainer>
    </Panel>
  );
};
